/**
 * The chat-answer badge convention: `[[Label]]` in a streamed markdown answer renders as a `MetaChip`.
 *
 * A read answer is prose the model writes, so a status marker on a row ("this posted shift is a
 * REPLACE request") can only arrive as text. This is the one inline affordance that turns such a
 * marker into a real chip instead of stray punctuation. `[[...]]` is inert in GFM — remark leaves it
 * in the text node it belongs to — so no markdown feature is stolen, and an un-upgraded surface (the
 * Slack mirror) still shows a legible `[[Replace]]`.
 */

/** The hast tag the plugin emits; `ChatMarkdown` maps it to the chip. `mark` is unreachable from
 * markdown syntax (raw HTML is not parsed), so overriding it can never shadow real content. */
export const CHAT_BADGE_TAG = "mark";

/** A short marker only: 1–24 chars, no brackets or newlines. A sentence in brackets stays text. */
const BADGE_PATTERN = /\[\[([^[\]\n]{1,24})\]\]/g;

type MdastNode = {
	type: string;
	value?: string;
	children?: MdastNode[];
	data?: { hName?: string };
};

const badgeNode = (label: string): MdastNode => ({
	// `emphasis` only to borrow a guaranteed inline to-hast handler; `hName` renames the element.
	type: "emphasis",
	children: [{ type: "text", value: label }],
	data: { hName: CHAT_BADGE_TAG },
});

/** Splits one text node into text/badge parts, or null when it holds no badge. */
const splitTextNode = (value: string): MdastNode[] | null => {
	BADGE_PATTERN.lastIndex = 0;
	const parts: MdastNode[] = [];
	let cursor = 0;
	for (let match = BADGE_PATTERN.exec(value); match !== null; match = BADGE_PATTERN.exec(value)) {
		if (match.index > cursor) parts.push({ type: "text", value: value.slice(cursor, match.index) });
		parts.push(badgeNode(match[1]));
		cursor = match.index + match[0].length;
	}
	if (parts.length === 0) return null;
	if (cursor < value.length) parts.push({ type: "text", value: value.slice(cursor) });
	return parts;
};

const walk = (node: MdastNode): void => {
	if (!node.children) return;
	const next: MdastNode[] = [];
	for (const child of node.children) {
		// Inside `code`/`inlineCode` there are no text children to walk, so a literal `[[x]]` in a code
		// span survives as written — the escape hatch for showing the syntax itself.
		if (child.type === "text" && child.value !== undefined) {
			const parts = splitTextNode(child.value);
			next.push(...(parts ?? [child]));
			continue;
		}
		walk(child);
		next.push(child);
	}
	node.children = next;
};

/** remark plugin: rewrites `[[Label]]` in text into the badge element `ChatMarkdown` renders as a chip. */
export const remarkChatBadges = () => (tree: MdastNode) => {
	walk(tree);
};
